import { useState, useEffect } from 'react'
import { ParkingCircle, CheckCircle2, XCircle, TrendingUp, Car, Clock, ChevronLeft, ChevronRight } from 'lucide-react'
import './ParkingSlots.css'
import { elmecLogo, englishFlag, italyFlag } from '../../assets'
import { allPaths, translations } from '../../utils/constants'
import { useHistory, useParams } from 'react-router-dom/cjs/react-router-dom.min'
import { setDefaultLanguage } from '../../Redux/actions/defaultLanguageAction'
import { useDispatch, useSelector } from 'react-redux'
import { Select } from 'antd'
import { secureApi } from '../../Config/api'
import { GET } from '../../utils/apis'
import io from 'socket.io-client'

const { Option } = Select

const SLOTS_PER_PAGE = 50
const PAGE_ROTATE_INTERVAL = 10000

export default function ParkingLot() {
    const history = useHistory()
    const dispatch = useDispatch()
    const { organizationId } = useParams()
    const language = useSelector((state) => state.defaultLanguageReducer?.language)

    const [slots, setSlots] = useState([])
    const [organizationName, setOrganizationName] = useState('')
    const [currentTime, setCurrentTime] = useState(new Date())
    const [currentPage, setCurrentPage] = useState(0)
    const [parkingStats, setParkingStats] = useState({
        totalSpots: 0,
        occupiedSpots: 0,
        freeSpots: 0,
        occupancyRate: 0
    })
    const [loading, setLoading] = useState(true)

    const handleLanguageChange = (value) => {
        dispatch(setDefaultLanguage(value))
    }

    const fetchParkingSlots = async () => {
        try {
            setLoading(true)

            const response = await secureApi.get(GET?.GET_PARKING_SLOTS, {
                params: {
                    organization_id: organizationId
                }
            })

            const result = response?.data || response

            console.log('Parking slots response:', result)

            let data = {}

            if (Array.isArray(result)) {
                data = result?.[0] || {}
            }
            else if (Array.isArray(result?.data)) {
                data = result?.data?.[0] || {}
            }
            else {
                data = result?.data || result || {}
            }

            const orgName = data?.organization_name || ''
            const organizationLetter = orgName ? orgName.charAt(0).toUpperCase() : 'A'

            setOrganizationName(orgName)

            const total = Number(data?.total_spaces || data?.totalSpots || 0)
            const occupiedVehicles = data?.occupiedVehicles || []
            const occupied = Number(data?.occupiedSpots || occupiedVehicles?.length || 0)
            const free = Number(data?.freeSpots ?? (total - occupied))
            const occupancyRate = Number(
                data?.occupancyRate ?? (total ? Math.round((occupied / total) * 100) : 0)
            )

            setParkingStats({
                totalSpots: total,
                occupiedSpots: occupied,
                freeSpots: free,
                occupancyRate
            })

            // Index occupied vehicles by numeric slot — handles "1", 1, "T1" etc.
            const occupiedBySlot = new Map()
            occupiedVehicles.forEach(v => {
                const raw = v?.parking_slot_no ?? v?.slotNo
                if (raw === null || raw === undefined || raw === '') return
                const slotNumber = parseInt(String(raw).replace(/\D/g, ''), 10)
                if (!isNaN(slotNumber) && !occupiedBySlot.has(slotNumber)) {
                    occupiedBySlot.set(slotNumber, v)
                }
            })

            const newSlots = []

            for (let i = 1; i <= total; i++) {
                const slotId = `${organizationLetter}${i}`
                const occupiedVehicle = occupiedBySlot.get(i)

                newSlots.push({
                    id: slotId,
                    status: occupiedVehicle ? 'occupied' : 'available',
                    plate: occupiedVehicle?.corrected_plate_number || occupiedVehicle?.plate_number || occupiedVehicle?.original_plate_number || '',
                    slotNo: occupiedVehicle?.parking_slot_no || '',
                    entryTime: occupiedVehicle?.entry_time || null
                })
            }

            setSlots(newSlots)
        }
        catch (err) {
            console.error('Parking slots error:', err)
            setSlots([])
            setParkingStats({
                totalSpots: 0,
                occupiedSpots: 0,
                freeSpots: 0,
                occupancyRate: 0
            })
        }
        finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (!organizationId) {
            return
        }

        fetchParkingSlots()

        // Live updates — refetch whenever a parking session is created/updated
        const socket = io()
        const refresh = () => fetchParkingSlots()

        socket.on('parking_session_created', refresh)
        socket.on('parking_session_updated', refresh)
        // catch up on events missed while the socket was disconnected
        socket.io.on('reconnect', refresh)

        return () => {
            socket.off('parking_session_created', refresh)
            socket.off('parking_session_updated', refresh)
            socket.io.off('reconnect', refresh)
            socket.disconnect()
        }
    }, [organizationId])

    useEffect(() => {
        const clockTimer = setInterval(() => setCurrentTime(new Date()), 1000)
        return () => clearInterval(clockTimer)
    }, [])

    const formattedTime = currentTime.toLocaleTimeString(language === 'it' ? 'it-IT' : 'en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    })

    const formattedDate = currentTime.toLocaleDateString(language === 'it' ? 'it-IT' : 'en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    })

    const occupancyDegrees = Math.min(parkingStats.occupancyRate, 100) * 3.6

    // Max 50 slots per page so tiles stay large enough for plate numbers;
    // extra slots rotate in like a carousel
    const slotPages = []
    for (let i = 0; i < slots.length; i += SLOTS_PER_PAGE) {
        slotPages.push(slots.slice(i, i + SLOTS_PER_PAGE))
    }
    const totalPages = slotPages.length

    useEffect(() => {
        setCurrentPage(0)

        if (totalPages <= 1) {
            return
        }

        const rotateTimer = setInterval(() => {
            setCurrentPage(prev => (prev + 1) % totalPages)
        }, PAGE_ROTATE_INTERVAL)

        return () => clearInterval(rotateTimer)
    }, [totalPages])

    // Starts from the ideal column count for the screen's aspect ratio, then
    const getColumnsForCount = (count) => {
        if (!count) return 1

        const ideal = Math.min(Math.max(Math.ceil(Math.sqrt(count * 2.4)), 4), 16)

        let best = ideal
        let bestEmpty = (ideal - (count % ideal)) % ideal

        for (let offset = 1; offset <= 2; offset++) {
            for (const candidate of [ideal - offset, ideal + offset]) {
                if (candidate < 4 || candidate > 16) continue
                const empty = (candidate - (count % candidate)) % candidate
                if (empty < bestEmpty) {
                    best = candidate
                    bestEmpty = empty
                }
            }
        }

        return best
    }

    const formatParkedDuration = (entryTime) => {
        const start = new Date(entryTime).getTime()
        if (isNaN(start)) return ''

        const totalMinutes = Math.max(0, Math.floor((currentTime.getTime() - start) / 60000))
        const days = Math.floor(totalMinutes / 1440)
        const hours = Math.floor((totalMinutes % 1440) / 60)
        const minutes = totalMinutes % 60

        if (days > 0) return `${days}d ${hours}h`
        if (hours > 0) return `${hours}h ${minutes}m`
        return `${minutes}m`
    }

    const showInitialSkeleton = loading && slots.length === 0

    const renderStatsSkeleton = () => (
        <section className='led-stats'>
            {[1, 2, 3, 4].map((item) => (
                <div className='led-stat-card' key={item}>
                    <div className='skeleton skeleton-icon'></div>
                    <div className='led-stat-info'>
                        <div className='skeleton skeleton-label'></div>
                        <div className='skeleton skeleton-value'></div>
                    </div>
                </div>
            ))}
        </section>
    )

    const renderSlotsSkeleton = () => (
        <div className='led-carousel'>
            <div className='led-slots-grid' style={{ gridTemplateColumns: 'repeat(10, 1fr)' }}>
                {Array.from({ length: 30 }).map((_, index) => (
                    <div className='led-slot led-slot-skeleton' key={index}>
                        <div className='led-slot-top'>
                            <div className='skeleton skeleton-slot-name'></div>
                            <div className='skeleton skeleton-slot-icon'></div>
                        </div>
                        <div className='skeleton skeleton-slot-status'></div>
                    </div>
                ))}
            </div>
        </div>
    )

    return (
        <div className='led-parking'>
            <header className='led-header'>
                <div className='led-header-left'>
                    <div className='led-logo-wrapper' style={{ cursor: 'pointer' }} onClick={() => history.push(allPaths?.HOME)}>
                        <img src={elmecLogo} alt='Elmec Elettronica' className='led-logo' />
                    </div>
                    <div className='led-header-divider'></div>
                    <div className='led-title-block'>
                        <div className='led-title-text'>
                            <span className='led-eyebrow'>{translations[language]?.parking}</span>
                            <h1 className='led-title'>{organizationName || translations[language]?.parking}</h1>
                        </div>
                        <div className='led-live-badge'>
                            <span className='led-live-dot'></span>
                            {translations[language]?.live}
                        </div>
                    </div>
                </div>
                <div className='led-header-right'>
                    <div className='led-clock-block'>
                        <p className='led-clock'>{formattedTime}</p>
                        <p className='led-date'>{formattedDate}</p>
                    </div>
                    <div className='led-lang-select'>
                        <Select
                            value={language}
                            style={{ width: 30 }}
                            onChange={handleLanguageChange}
                            suffixIcon={null}
                            dropdownMatchSelectWidth={false}
                            dropdownStyle={{ minWidth: 5, padding: 0 }}
                            className='flag-select'
                        >
                            <Option value='it'>
                                <img src={italyFlag} alt='Italian' style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }} />
                            </Option>
                            <Option value='en'>
                                <img src={englishFlag} alt='English' style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }} />
                            </Option>
                        </Select>
                    </div>
                </div>
            </header>

            <main className='led-main'>
                {showInitialSkeleton ? renderStatsSkeleton() : (
                    <section className='led-stats'>
                        <div className='led-stat-card led-stat-total'>
                            <div className='led-stat-icon'><ParkingCircle size={32} /></div>
                            <div className='led-stat-info'>
                                <p className='led-stat-label'>{translations[language]?.totalSlots}</p>
                                <p className='led-stat-value'>{parkingStats.totalSpots}</p>
                            </div>
                            <div className='led-stat-glow'></div>
                        </div>
                        <div className='led-stat-card led-stat-available'>
                            <div className='led-stat-icon'><CheckCircle2 size={32} /></div>
                            <div className='led-stat-info'>
                                <p className='led-stat-label'>{translations[language]?.available}</p>
                                <p className='led-stat-value'>{parkingStats.freeSpots}</p>
                            </div>
                            <div className='led-stat-glow'></div>
                        </div>
                        <div className='led-stat-card led-stat-occupied'>
                            <div className='led-stat-icon'><XCircle size={32} /></div>
                            <div className='led-stat-info'>
                                <p className='led-stat-label'>{translations[language]?.occupied}</p>
                                <p className='led-stat-value'>{parkingStats.occupiedSpots}</p>
                            </div>
                            <div className='led-stat-glow'></div>
                        </div>
                        <div className='led-stat-card led-stat-rate'>
                            <div
                                className='led-occupancy-ring'
                                style={{ background: `conic-gradient(#ef3e3e ${occupancyDegrees}deg, rgba(255,255,255,0.08) ${occupancyDegrees}deg)` }}
                            >
                                <div className='led-occupancy-inner'>
                                    <TrendingUp size={18} />
                                    <span>{parkingStats.occupancyRate}%</span>
                                </div>
                            </div>
                            <div className='led-stat-info'>
                                <p className='led-stat-label'>{translations[language]?.occupancy}</p>
                                <p className='led-stat-sub'>
                                    {parkingStats.occupiedSpots} / {parkingStats.totalSpots} {translations[language]?.inUse}
                                </p>
                            </div>
                            <div className='led-stat-glow'></div>
                        </div>
                    </section>
                )}

                <section className='led-board'>
                    <div className='led-board-header'>
                        <div className='led-board-title'>
                            <Car size={24} />
                            <h2>{translations[language]?.parkingSlotsHeading}</h2>
                        </div>
                        <div className='led-board-header-right'>
                            {totalPages > 1 && (
                                <div className='led-page-indicator'>
                                    <button
                                        type='button'
                                        className='led-page-arrow'
                                        onClick={() => setCurrentPage((currentPage - 1 + totalPages) % totalPages)}
                                        aria-label='Previous page'
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    {slotPages.map((_, index) => (
                                        <button
                                            key={index}
                                            className={`led-page-dot ${index === currentPage ? 'active' : ''}`}
                                            onClick={() => setCurrentPage(index)}
                                            aria-label={`Page ${index + 1}`}
                                        />
                                    ))}
                                    <span className='led-page-count'>{currentPage + 1} / {totalPages}</span>
                                    <button
                                        type='button'
                                        className='led-page-arrow'
                                        onClick={() => setCurrentPage((currentPage + 1) % totalPages)}
                                        aria-label='Next page'
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            )}
                            <div className='led-legend'>
                                <div className='led-legend-item led-legend-available'>
                                    <span className='led-legend-dot'></span>
                                    {translations[language]?.available}
                                </div>
                                <div className='led-legend-item led-legend-occupied'>
                                    <span className='led-legend-dot'></span>
                                    {translations[language]?.occupied}
                                </div>
                            </div>
                        </div>
                    </div>

                    {showInitialSkeleton ? renderSlotsSkeleton() : (
                        <div className='led-carousel'>
                            <div
                                className='led-carousel-track'
                                style={{ transform: `translateX(-${currentPage * 100}%)` }}
                            >
                                {slotPages.map((page, pageIndex) => (
                                    <div
                                        key={pageIndex}
                                        className='led-slots-grid'
                                        style={{ gridTemplateColumns: `repeat(${getColumnsForCount(page.length)}, 1fr)` }}
                                    >
                                        {page.map((slot) => (
                                            <div
                                                key={slot.id}
                                                className={`led-slot ${slot.status}`}
                                                title={`${slot.id} - ${slot.status === 'available' ? 'Available' : 'Occupied'}`}
                                            >
                                                <div className='led-slot-top'>
                                                    <span className='led-slot-name'>{slot.id}</span>
                                                    {slot.status === 'available'
                                                        ? <CheckCircle2 size={20} strokeWidth={2.4} className='led-slot-status-icon' />
                                                        : (
                                                            <span className='led-slot-occupied-badge'>
                                                                <Car size={20} strokeWidth={2.4} className='led-slot-status-icon' />
                                                            </span>
                                                        )}
                                                </div>
                                                {slot.status === 'occupied' && slot.plate ? (
                                                    <span className='led-slot-plate'>{slot.plate}</span>
                                                ) : (
                                                    <span className='led-slot-state'>
                                                        {slot.status === 'available' ? translations[language]?.free : translations[language]?.busy}
                                                    </span>
                                                )}
                                                {slot.status === 'occupied' && slot.entryTime && (
                                                    <span className='led-slot-time'>
                                                        <Clock size={12} strokeWidth={2.5} />
                                                        {formatParkedDuration(slot.entryTime)}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ))}

                                {!loading && slots.length === 0 && (
                                    <div className='led-empty'>{translations[language]?.noSlotsConfigured}</div>
                                )}
                            </div>
                        </div>
                    )}
                </section>
            </main>
        </div>
    )
}